/**
 * 测试场景库
 * 
 * 每个场景包含以下字段：
 * - id: 场景唯一标识符
 * - title: 场景标题
 * - description: 场景详细描述
 * - imagePlaceholder: 图片占位符URL
 * - tags: 标签数组，用于与用户问卷结果进行匹配
 *   - personality: 性格特点相关标签
 *   - challenges: 社交挑战相关标签
 *   - goals: 学习目标相关标签
 *   - context: 场景环境相关标签
 *   - difficulty: 难度级别 ['beginner', 'intermediate', 'advanced']
 */

const scenarioLibrary = [
  {
    id: "presentation-proposal",
    title: "Project Presentation Proposal",
    description: "You need to present an important project to the senior management team. This project is being developed by you and is crucial for the company's future growth. The presentation will last for 15 minutes, followed by a 5-minute Q&A session. During previous presentations, several managers have shown skepticism about this project. However, if the project gets approved, it will significantly benefit your career development. Your tasks are: 1. How would you prepare for this presentation? 2. What strategies would you use during the presentation to address the skeptical managers? 3. How would you respond if someone interrupts your presentation with criticism?",
    imagePlaceholder: "presentation.jpg",
    tags: {
      difficulty: "intermediate",
      context: ["workplace", "professional"],
      goals: ["publicSpeaking", "leadership", "careerAdvancement"],
      personality: ["analytical", "direct"],
      challenges: ["publicSpeaking", "conflictResolution"]
    }
  },
  {
    id: "team-conflict",
    title: "Team Conflict Resolution",
    description: "You're leading a project team where two key members are in constant disagreement. Their conflict is affecting team morale and project progress. One team member is very detail-oriented and focuses on process adherence, while the other is more results-focused and believes in flexibility. The project deadline is approaching, and you need both members to collaborate effectively. How would you approach this situation to resolve the conflict while maintaining a positive team dynamic?",
    imagePlaceholder: "team-conflict.jpg",
    tags: {
      difficulty: "advanced",
      context: ["workplace", "professional"],
      goals: ["leadership", "conflictResolution"],
      personality: ["empathetic", "supportive"],
      challenges: ["conflictResolution", "leadership"]
    }
  },
  {
    id: "networking-event",
    title: "Industry Networking Event",
    description: "You're attending a major industry conference where many influential people in your field will be present. You're relatively new to the industry and don't know many people yet. You want to establish meaningful connections that could help your career growth, but you're naturally reserved in large social gatherings. What approach would you take to network effectively at this event while staying authentic to your personality?",
    imagePlaceholder: "networking.jpg",
    tags: {
      difficulty: "intermediate",
      context: ["professional", "socialSettings"],
      goals: ["networking", "careerAdvancement"],
      personality: ["reserved", "thoughtful"],
      challenges: ["networking", "smallTalk"]
    }
  },
  {
    id: "difficult-feedback",
    title: "Delivering Difficult Feedback",
    description: "You need to provide constructive feedback to a colleague whose work quality has been declining recently. This person is generally sensitive to criticism and has been going through some personal difficulties that you're vaguely aware of. However, their performance issues are affecting the team's output, and you need to address it. How would you approach this conversation to ensure the feedback is received constructively while being sensitive to their situation?",
    imagePlaceholder: "feedback.jpg",
    tags: {
      difficulty: "advanced",
      context: ["workplace", "personalRelationships"],
      goals: ["leadership", "betterRelationships"],
      personality: ["empathetic", "supportive"],
      challenges: ["conflictResolution", "receivingFeedback"]
    }
  },
  {
    id: "job-interview",
    title: "Job Interview Preparation",
    description: "You've been invited to a final interview for your dream job. The position would be a significant step up in your career. You know that several other qualified candidates are being considered. During the interview, you'll meet with the CEO and several senior team members. You want to showcase your skills and experience while also demonstrating that you'd be a good cultural fit. How would you prepare for this interview, and what approach would you take during the meeting?",
    imagePlaceholder: "interview.jpg",
    tags: {
      difficulty: "intermediate",
      context: ["workplace", "professional"],
      goals: ["careerAdvancement", "confidence"],
      personality: ["analytical", "direct"],
      challenges: ["publicSpeaking"]
    }
  },
  {
    id: "public-speaking",
    title: "Impromptu Public Speaking",
    description: "You're attending an important conference in your field. After a speaker cancels at the last minute, the organizer, who knows your expertise, asks you to fill in with a 15-minute talk. You have only 30 minutes to prepare, and there will be about 100 people in the audience, including several respected figures in your industry. How would you handle this situation and deliver an effective talk with minimal preparation time?",
    imagePlaceholder: "public-speaking.jpg",
    tags: {
      difficulty: "advanced",
      context: ["professional", "publicSpeaking"],
      goals: ["publicSpeaking", "confidence"],
      personality: ["creative", "adaptable"],
      challenges: ["publicSpeaking"]
    }
  },
  {
    id: "difficult-client",
    title: "Dealing with a Difficult Client",
    description: "You're managing a project for a client who frequently changes requirements, criticizes your team's work, and communicates in a confrontational manner. Despite these challenges, this client is important to your company, and senior management wants to maintain the relationship. Your team is becoming demoralized by the client's behavior. How would you manage this situation to protect your team while preserving the client relationship?",
    imagePlaceholder: "difficult-client.jpg",
    tags: {
      difficulty: "advanced",
      context: ["workplace", "professional"],
      goals: ["leadership", "conflictResolution", "negotiation"],
      personality: ["empathetic", "direct"],
      challenges: ["conflictResolution", "leadership"]
    }
  },
  {
    id: "social-event",
    title: "Navigating a Social Gathering",
    description: "You've been invited to a social gathering where you only know the host. Most attendees are professionals in fields related to yours, and this could be a good opportunity for both personal and professional connections. You generally find it challenging to start conversations with new people, but you want to make the most of this event. How would you approach this situation to engage meaningfully with others while feeling comfortable?",
    imagePlaceholder: "social-gathering.jpg",
    tags: {
      difficulty: "beginner",
      context: ["socialSettings", "personalRelationships"],
      goals: ["betterRelationships", "networking"],
      personality: ["reserved", "thoughtful"],
      challenges: ["smallTalk", "networking", "socialEvents"]
    }
  },
  {
    id: "team-motivation",
    title: "Motivating a Demoralized Team",
    description: "You're leading a team that has just experienced a significant setback. A project they worked on for months was canceled due to budget cuts. Team morale is very low, and some members are questioning the value of their work. You need to remotivate the team and redirect their efforts to other important initiatives. How would you approach this challenge to rebuild enthusiasm and focus?",
    imagePlaceholder: "team-motivation.jpg",
    tags: {
      difficulty: "advanced",
      context: ["workplace", "professional"],
      goals: ["leadership", "betterRelationships"],
      personality: ["empathetic", "supportive"],
      challenges: ["leadership"]
    }
  },
  {
    id: "group-discussion",
    title: "Contributing to Group Discussion",
    description: "You're part of a cross-functional team discussing an important strategic initiative. You have valuable insights to contribute, but several dominant personalities keep controlling the conversation. When you try to speak, you often get interrupted or your points are overlooked. How would you ensure your ideas are heard and considered in this environment without becoming confrontational?",
    imagePlaceholder: "group-discussion.jpg",
    tags: {
      difficulty: "intermediate",
      context: ["workplace", "professional"],
      goals: ["assertiveness", "confidence"],
      personality: ["reserved", "analytical"],
      challenges: ["groupDiscussions", "assertiveness"]
    }
  },
  {
    id: "friendship-boundaries",
    title: "Setting Boundaries in Friendship",
    description: "You have a close friend who has been increasingly relying on you for emotional support during a difficult time in their life. While you want to be there for them, their constant need for your time and attention is affecting your well-being and other responsibilities. You value the friendship but need to establish healthier boundaries. How would you communicate your needs while being supportive of your friend?",
    imagePlaceholder: "friendship.jpg",
    tags: {
      difficulty: "beginner",
      context: ["personalRelationships"],
      goals: ["betterRelationships", "assertiveness"],
      personality: ["empathetic", "supportive"],
      challenges: ["assertiveness", "relationships"]
    }
  },
  {
    id: "salary-negotiation",
    title: "Negotiating a Salary Increase",
    description: "You've been in your current role for two years, consistently exceeding expectations and taking on additional responsibilities. You believe you're deserving of a significant salary increase, but your company has recently implemented budget constraints. You have a meeting scheduled with your manager to discuss compensation. How would you prepare for and approach this conversation to maximize your chances of success?",
    imagePlaceholder: "negotiation.jpg",
    tags: {
      difficulty: "intermediate",
      context: ["workplace", "professional"],
      goals: ["negotiation", "careerAdvancement"],
      personality: ["direct", "analytical"],
      challenges: ["negotiations", "assertiveness"]
    }
  }
];

export default scenarioLibrary; 